import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../../../services/api';
import { FiArrowLeft } from 'react-icons/fi';
import { useLanguage } from '../../../contexts/LanguageContext';
import {
  Container,
  Header,
  Title,
  BackButton,
  DivDetailCard,
  DetailCard,
  DetailItem,
  DetailLabel,
  DetailValue,
  DetailSection,
  SectionTitle,
  SeverityBadge,
  StatusBadge,
  ParametersContainer
} from './styles';
import Tooltip from '../../../components/tooltip';
import Loading from '../../../components/Loading';

interface Rule {
  _id: string;
  name: string;
  description: string;
  prompt?: string;
  category: string;
  severity: string;
  status: string;
  parameters?: string;
  createdAt?: string;
  updatedAt?: string;
}

const RuleDetails: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { id } = useParams<{ id: string }>();
  const [rule, setRule] = useState<Rule | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const fetchRef = useRef(false);

  useEffect(() => {
    const fetchRule = async () => {
      if (fetchRef.current) return;
      fetchRef.current = true;

      setIsLoading(true);
      try {
        console.log('Fetching rule details for ID:', id);
        const token = localStorage.getItem('@App:token');
        if (!token) {
          console.error('No auth token found');
          setIsLoading(false);
          return;
        }

        const response = await api.get(`/api/prompt/${id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        console.log('Rule data received:', response.data);
        setRule(response.data);
      } catch (error: any) {
        console.error('Error loading rule from API:', error);

        // If there's no auth, redirect to login
        if (error.response && error.response.status === 401) {
          navigate('/login');
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchRule();
  }, [id, navigate]);

  const handleBack = () => {
    navigate('/rules');
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Container>
      <Header>
        <Tooltip text={t('form.rule.backToRules')}>
          <BackButton onClick={handleBack}>
            {FiArrowLeft({ className: "icon", size: 30 })}
          </BackButton>
        </Tooltip>
      </Header>

      {isLoading ? (
        <Loading />
      ) : rule ? (
        <DivDetailCard>
          <Title>{t('form.rule.detailsOfRule')}</Title>

          <DetailCard>
            <DetailSection>
              <DetailItem>
                <DetailLabel>{t('form.rule.name')}</DetailLabel>
                <DetailValue>{rule.name}</DetailValue>
              </DetailItem>
              
              <DetailItem>
                <DetailLabel>{t('form.rule.prompt')}</DetailLabel>
                <DetailValue>{rule.prompt}</DetailValue>
              </DetailItem>
            </DetailSection>
          </DetailCard>
        </DivDetailCard>
      ) : (
        <div style={{ textAlign: 'center', marginTop: '2rem' }}>
          <h2>{t('form.rule.ruleNotFound')}</h2>
          <p>{t('form.rule.ruleNotFoundDesc')}</p>
        </div>
      )}
    </Container>
  );
};

export default RuleDetails; 